import React from "react";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import {
  contactsFacExportFields,
  firmographicsExportFields,
} from "./exportConfigurationFields";
import { EXPORT_INCLUSION_TYPES } from "./types";
import { useProductFilterRenderer } from "./hooks/useProductFilterRenderer";
import type { ContactsFacExportSettings } from "@/lib/company-filter/export/universePayloadBuilder";

const DATA_TYPE = "Contacts + Tech Installs";

// Matches OppGen's default once "Limit contacts per company" is enabled.
const DEFAULT_CONTACTS_FAC_LIMIT = 20;

interface Props {
  settings: ContactsFacExportSettings;
  setSettings: (settings: ContactsFacExportSettings) => void;
}

export const ContactsFacExportOptions: React.FC<Props> = ({
  settings,
  setSettings,
}) => {
  const disabled = settings.inclusion === EXPORT_INCLUSION_TYPES.NONE;
  const limitEnabled = settings.contactsPerCompanyLimit !== null;

  const renderOptionContent = useProductFilterRenderer(
    settings.inclusion,
    settings.productIds,
    (productIds) => setSettings({ ...settings, productIds })
  );

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        {DATA_TYPE}
      </Typography>
      <ExportDataTypeInclusionSelector
        dataType={DATA_TYPE}
        options={[
          {
            label: "Do not include any Contacts + Tech Installs",
            value: EXPORT_INCLUSION_TYPES.NONE,
          },
          {
            label: "Include contacts at installs of the products in my filters",
            value: EXPORT_INCLUSION_TYPES.FILTERS,
          },
          {
            label: "Include contacts at installs of selected products",
            value: EXPORT_INCLUSION_TYPES.SELECTED,
          },
          {
            label: "Include contacts at all product installs",
            value: EXPORT_INCLUSION_TYPES.ALL,
          },
        ]}
        inclusion={settings.inclusion}
        setInclusion={(inclusion) =>
          setSettings({
            ...settings,
            inclusion,
            productIds:
              inclusion === EXPORT_INCLUSION_TYPES.SELECTED
                ? settings.productIds
                : [],
          })
        }
        renderOptionContent={renderOptionContent}
      />
      {!disabled && (
        <Alert severity="info">
          This dataset is a subset of your Contacts and Tech Installs feeds.
          Record counts will be lower than either standalone feed. Each row is
          one contact at one product install, so a contact appears once per
          matched product. The Contacts filters apply to this export.
        </Alert>
      )}
      <Stack>
        <FormControlLabel
          label="Limit contacts per company"
          control={
            <Checkbox
              checked={limitEnabled}
              disabled={disabled}
              onChange={() =>
                setSettings({
                  ...settings,
                  contactsPerCompanyLimit: limitEnabled
                    ? null
                    : DEFAULT_CONTACTS_FAC_LIMIT,
                })
              }
              data-testid="contacts-fac-limit-per-company-checkbox"
            />
          }
        />
        {limitEnabled && (
          <Stack sx={{ pl: 1.5, pt: 1 }} spacing={0.5}>
            <TextField
              label="Max contacts per company"
              type="number"
              size="small"
              value={settings.contactsPerCompanyLimit ?? ""}
              disabled={disabled}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  contactsPerCompanyLimit: Math.max(1, Number(e.target.value)),
                })
              }
              inputProps={{
                min: 1,
                "data-testid": "contacts-fac-per-company-limit-input",
              }}
              sx={{ maxWidth: 200 }}
            />
            <Typography variant="caption" color="text.secondary">
              Caps the number of contacts (not rows) exported per company. The
              most recently verified contacts are kept, with all of their
              product rows.
            </Typography>
          </Stack>
        )}
      </Stack>
      <FieldConfigurationSelector
        fields={contactsFacExportFields}
        firmographicsFields={firmographicsExportFields}
        value={settings.configuration}
        onChange={(configuration) =>
          setSettings({ ...settings, configuration })
        }
        title="Contacts + Tech Installs Fields"
        checkboxLabel="Export All Contacts + Tech Installs Fields"
        disabled={disabled}
      />
    </Stack>
  );
};
